import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useUser } from '@clerk/clerk-react'
import { useThemeColors } from '../hooks/useThemeColors'
import {
  getAdminLeads,
  updateLead,
  logInteraction,
  createLead,
  makeLeadPrimary,
  triggerCalcomEngagedLead,
  type Lead,
  type NewInteraction,
  type NewLead,
} from '../lib/api'

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai']

const CHANNEL_ICONS: Record<string, string> = {
  call: '📞',
  email: '✉',
  linkedin: '💼',
  meeting: '🤝',
  demo: '🖥',
  note: '📝',
  discovery_call: '🔍',
  other: '💬',
}

const VERTICALS = ['Smart Camera', 'AV', 'Drones', 'Robots', 'Defense', 'Video Ad Tech', 'Other']
const LEAD_VERTICALS = ['Smart Camera', 'AV', 'Drones', 'Robots', 'Defense', 'Video Ad Tech', 'Other'] as const
type LeadVertical = typeof LEAD_VERTICALS[number]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const days = Math.floor(diff / 86400000)
  if (days < 1) return 'today'
  if (days === 1) return '1d ago'
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}

function isPlaceholderEmail(email: string): boolean {
  return email?.includes('@placeholder.primateintelligence.ai') || false
}

// ─── Add Lead Modal ───────────────────────────────────────────────────────────

interface AddLeadModalProps {
  onClose: () => void
  onSuccess: (lead: Lead) => void
}

function AddLeadModal({ onClose, onSuccess }: AddLeadModalProps) {
  const [company, setCompany] = useState('')
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [title, setTitle] = useState('')
  const [website, setWebsite] = useState('')
  const [vertical, setVertical] = useState<LeadVertical | ''>('')
  const [introducedBy, setIntroducedBy] = useState('')
  const [nextAction, setNextAction] = useState('')
  const [notes, setNotes] = useState('')
  const [status, setStatus] = useState<'prospect' | 'engaged'>('prospect')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit() {
    if (!company.trim()) {
      setError('Company is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload: NewLead = { company: company.trim(), status }
      if (fullName.trim()) payload.full_name = fullName.trim()
      if (email.trim()) payload.email = email.trim()
      if (title.trim()) payload.contact_title = title.trim()
      if (website.trim()) payload.website = website.trim()
      if (vertical) payload.vertical = vertical as NewLead['vertical']
      if (introducedBy.trim()) payload.introduced_by = introducedBy.trim()
      if (nextAction.trim()) payload.next_action = nextAction.trim()
      if (notes.trim()) payload.notes = notes.trim()
      const lead = await createLead(payload)
      onSuccess(lead)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-void/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-obsidian border border-carbon rounded w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-carbon flex items-center justify-between sticky top-0 bg-obsidian">
          <h2 className="text-h3 text-chalk">Add Lead</h2>
          <button onClick={onClose} className="text-steel hover:text-chalk text-title-1 leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-footnote text-steel mb-1">Company <span className="text-red-400">*</span></label>
            <input type="text" value={company} onChange={e => setCompany(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Full name</label>
            <input type="text" value={fullName} onChange={e => setFullName(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Title</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. CTO, VP Engineering"
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Website</label>
            <input type="url" value={website} onChange={e => setWebsite(e.target.value)}
              placeholder="https://..."
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Vertical</label>
            <select value={vertical} onChange={e => setVertical(e.target.value as LeadVertical | '')}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
              <option value="">— Select vertical —</option>
              {LEAD_VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Introduced by</label>
            <input type="text" value={introducedBy} onChange={e => setIntroducedBy(e.target.value)}
              placeholder="e.g. Rick McKenzie, Initialized Capital"
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Next action</label>
            <input type="text" value={nextAction} onChange={e => setNextAction(e.target.value)}
              placeholder="e.g. Book intro call"
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="What do they do? Why are they a fit?"
              rows={3}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel focus:outline-none focus:border-signal resize-none" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value as 'prospect' | 'engaged')}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
              <option value="prospect">Prospect</option>
              <option value="engaged">Engaged</option>
            </select>
          </div>
          {error && <div className="text-footnote text-red-400 bg-red-900/20 rounded px-3 py-2">{error}</div>}
        </div>
        <div className="p-5 border-t border-carbon flex gap-3 justify-end sticky bottom-0 bg-obsidian">
          <button onClick={onClose}
            className="px-4 py-2 text-body-sm text-steel hover:text-chalk border border-carbon rounded transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-2 text-body-sm bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {saving ? 'Adding…' : 'Add Lead'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Edit Lead Panel ──────────────────────────────────────────────────────────

interface EditLeadPanelProps {
  lead: Lead
  onClose: () => void
  onSaved: (updated: Lead) => void
}

function EditLeadPanel({ lead, onClose, onSaved }: EditLeadPanelProps) {
  const [company, setCompany] = useState(lead.company ?? '')
  const [fullName, setFullName] = useState(lead.full_name ?? '')
  const [email, setEmail] = useState(isPlaceholderEmail(lead.email ?? '') ? '' : (lead.email ?? ''))
  const [title, setTitle] = useState(lead.contact_title ?? '')
  const [website, setWebsite] = useState(lead.website ?? '')
  const [vertical, setVertical] = useState(lead.vertical ?? '')
  const [introducedBy, setIntroducedBy] = useState(lead.introduced_by ?? '')
  const [nextAction, setNextAction] = useState(lead.next_action ?? '')
  const [whatTheyDo, setWhatTheyDo] = useState(lead.what_they_do ?? '')
  const [status, setStatus] = useState(lead.status ?? 'prospect')
  const [winLoss, setWinLoss] = useState(lead.win_loss_signal ?? '')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notesSaved, setNotesSaved] = useState(false)
  const [savedNotes, setSavedNotes] = useState<{ text: string; ts: string }[]>([])

  // Log note as an interaction
  async function handleSaveNote() {
    if (!notes.trim()) return
    setSaving(true)
    setError(null)
    try {
      await logInteraction(lead.id, {
        channel: 'note',
        direction: 'outbound',
        notes: notes.trim(),
        outcome: 'follow_up',
      } as NewInteraction)
      setSavedNotes(prev => [{ text: notes.trim(), ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }, ...prev])
      setNotes('')
      setNotesSaved(true)
      setTimeout(() => setNotesSaved(false), 2000)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  async function handleSave() {
    if (!company.trim()) {
      setError('Company is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const fields: Record<string, unknown> = {
        company: company.trim(),
        full_name: fullName.trim() || null,
        email: email.trim() || null,
        contact_title: title.trim() || null,
        website: website.trim() || null,
        vertical: vertical || null,
        introduced_by: introducedBy.trim() || null,
        next_action: nextAction.trim() || null,
        what_they_do: whatTheyDo.trim() || null,
        status,
        win_loss_signal: winLoss || null,
      }
      await updateLead(lead.id, fields)
      onSaved({ ...lead, ...fields } as Lead)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-obsidian border-l border-carbon w-full max-w-md h-full overflow-y-auto shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-carbon sticky top-0 bg-obsidian z-10">
          <div>
            <h2 className="text-body font-semibold text-chalk">Edit Lead</h2>
            <p className="text-footnote text-steel mt-0.5">{lead.company}</p>
          </div>
          <button onClick={onClose} className="text-steel hover:text-chalk text-title-1">✕</button>
        </div>

        <div className="flex-1 px-6 py-4 space-y-4 overflow-y-auto">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded px-3 py-2 text-red-400 text-footnote">{error}</div>
          )}

          {/* Quick note */}
          <div className="bg-signal/5 border border-signal/20 rounded p-3 space-y-2">
            <label className="block text-footnote text-signal font-medium">📝 Add a note</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Quick note or update…"
              rows={2}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal resize-none"
            />
            <button
              onClick={handleSaveNote}
              disabled={saving || !notes.trim()}
              className="px-3 py-1.5 text-footnote bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-40 transition-colors"
            >
              {notesSaved ? '✓ Saved' : 'Log Note'}
            </button>
            {savedNotes.length > 0 && (
              <div className="space-y-1 pt-1 border-t border-signal/20">
                {savedNotes.map((n, i) => (
                  <div key={i} className="flex gap-2 text-footnote">
                    <span className="text-steel shrink-0">{n.ts}</span>
                    <span className="text-chalk">{n.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-carbon pt-4">
            <div className="text-footnote text-steel font-medium uppercase tracking-wide mb-3">Contact Details</div>
          </div>

          <div>
            <label className="block text-footnote text-steel mb-1">Company <span className="text-red-400">*</span></label>
            <input type="text" value={company} onChange={e => setCompany(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Full name</label>
            <input type="text" value={fullName} onChange={e => setFullName(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Title</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. CTO, VP Engineering"
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Website</label>
            <input type="url" value={website} onChange={e => setWebsite(e.target.value)}
              placeholder="https://..."
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal" />
          </div>

          <div className="border-t border-carbon pt-4">
            <div className="text-footnote text-steel font-medium uppercase tracking-wide mb-3">Classification</div>
          </div>

          <div>
            <label className="block text-footnote text-steel mb-1">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
              <option value="prospect">Prospect</option>
              <option value="engaged">Engaged</option>
              <option value="disqualified">Disqualified</option>
            </select>
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Vertical</label>
            <select value={vertical} onChange={e => setVertical(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
              <option value="">— None —</option>
              {LEAD_VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Signal</label>
            <select value={winLoss} onChange={e => setWinLoss(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
              <option value="">— Not set —</option>
              <option value="win">Win</option>
              <option value="loss">Loss</option>
              <option value="pending">Pending</option>
              <option value="nurture">Nurture</option>
            </select>
          </div>

          <div className="border-t border-carbon pt-4">
            <div className="text-footnote text-steel font-medium uppercase tracking-wide mb-3">Context</div>
          </div>

          <div>
            <label className="block text-footnote text-steel mb-1">What they do</label>
            <textarea value={whatTheyDo} onChange={e => setWhatTheyDo(e.target.value)}
              placeholder="Brief description of their business"
              rows={2}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal resize-none" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Introduced by</label>
            <input type="text" value={introducedBy} onChange={e => setIntroducedBy(e.target.value)}
              placeholder="e.g. Rick McKenzie, Initialized Capital"
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Next action</label>
            <input type="text" value={nextAction} onChange={e => setNextAction(e.target.value)}
              placeholder="e.g. Book intro call"
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal" />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-carbon flex gap-3 sticky bottom-0 bg-obsidian">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-signal text-white px-4 py-2 rounded text-body-sm font-medium hover:bg-signal/90 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button onClick={onClose}
            className="px-4 py-2 text-steel hover:text-chalk text-body-sm transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Log Interaction Modal ────────────────────────────────────────────────────

interface LogInteractionModalProps {
  leadId: string
  leadName: string
  onClose: () => void
  onSuccess: () => void
}

const CHANNELS = ['call', 'email', 'linkedin', 'meeting', 'demo', 'note', 'discovery_call', 'other'] as const
const OUTCOMES = [
  { value: 'interested', label: 'Interested' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'no_response', label: 'No response' },
  { value: 'converted', label: 'Converted' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
] as const

function LogInteractionModal({ leadId, leadName, onClose, onSuccess }: LogInteractionModalProps) {
  const [channel, setChannel] = useState<NewInteraction['channel']>('call')
  const [direction, setDirection] = useState<'outbound' | 'inbound'>('outbound')
  const [notes, setNotes] = useState('')
  const [outcome, setOutcome] = useState<NewInteraction['outcome']>('no_response')
  const [occurredAt, setOccurredAt] = useState(() => new Date().toISOString().slice(0, 16))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [currentStack, setCurrentStack] = useState('')
  const [painPoints, setPainPoints] = useState('')
  const [desiredOutcome, setDesiredOutcome] = useState('')
  const [objections, setObjections] = useState('')
  const [questionsAsked, setQuestionsAsked] = useState('')
  const [budgetSignal, setBudgetSignal] = useState('')
  const [timeline, setTimeline] = useState('')
  const [demoRequested, setDemoRequested] = useState(false)
  const [designPartnerInterest, setDesignPartnerInterest] = useState('')
  const [winLoss, setWinLoss] = useState('')

  async function handleSave() {
    if (!notes.trim()) { setError('Notes are required'); return }
    setSaving(true)
    setError(null)
    try {
      const metadata: Record<string, unknown> = {}
      if (channel === 'discovery_call') {
        if (currentStack) metadata.current_stack = currentStack
        if (painPoints) metadata.pain_points = painPoints
        if (desiredOutcome) metadata.desired_outcome = desiredOutcome
        if (objections) metadata.objections = objections
        if (questionsAsked) metadata.questions_asked = questionsAsked
        if (budgetSignal) metadata.budget_signal = budgetSignal
        if (timeline) metadata.timeline = timeline
        if (demoRequested) metadata.demo_requested = demoRequested
        if (designPartnerInterest) metadata.design_partner_interest = designPartnerInterest
        if (winLoss) metadata.win_loss = winLoss
      }
      await logInteraction(leadId, {
        channel,
        direction,
        notes: notes.trim(),
        outcome,
        occurred_at: new Date(occurredAt).toISOString(),
        ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
      })
      onSuccess()
      onClose()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-void/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-obsidian border border-carbon rounded w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-carbon flex items-center justify-between">
          <div>
            <h2 className="text-h3 text-chalk">Log Interaction</h2>
            <p className="text-body-sm text-steel mt-0.5">{leadName}</p>
          </div>
          <button onClick={onClose} className="text-steel hover:text-chalk text-title-1 leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-footnote text-steel mb-2">Channel</label>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map(ch => (
                <button key={ch} onClick={() => setChannel(ch)}
                  className={`px-3 py-1.5 rounded text-footnote font-medium capitalize transition-colors ${
                    channel === ch ? 'bg-signal text-white' : 'bg-carbon/50 text-steel hover:text-chalk border border-carbon'
                  }`}>
                  {CHANNEL_ICONS[ch]} {ch}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-footnote text-steel mb-2">Direction</label>
            <div className="flex gap-2">
              {(['outbound', 'inbound'] as const).map(d => (
                <button key={d} onClick={() => setDirection(d)}
                  className={`px-3 py-1.5 rounded text-footnote font-medium capitalize transition-colors ${
                    direction === d ? 'bg-signal text-white' : 'bg-carbon/50 text-steel hover:text-chalk border border-carbon'
                  }`}>
                  {d === 'outbound' ? '↗ Outbound' : '↙ Inbound'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Date & Time</label>
            <input type="datetime-local" value={occurredAt} onChange={e => setOccurredAt(e.target.value)}
              className="bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Outcome</label>
            <select value={outcome} onChange={e => setOutcome(e.target.value as NewInteraction['outcome'])}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
              {OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Notes <span className="text-red-400">*</span></label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="What happened? Key takeaways…" rows={4}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal resize-none" />
          </div>
          {channel === 'discovery_call' && (
            <div className="border border-carbon/60 rounded p-4 space-y-4 bg-void/40">
              <div className="text-footnote text-signal font-medium uppercase tracking-wide">Discovery Call Details</div>
              <div>
                <label className="block text-footnote text-steel mb-1">Current stack</label>
                <input type="text" value={currentStack} onChange={e => setCurrentStack(e.target.value)}
                  placeholder="What tools/systems are they using today?"
                  className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal" />
              </div>
              <div>
                <label className="block text-footnote text-steel mb-1">Pain points</label>
                <textarea value={painPoints} onChange={e => setPainPoints(e.target.value)}
                  placeholder="What's breaking or frustrating them?" rows={2}
                  className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal resize-none" />
              </div>
              <div>
                <label className="block text-footnote text-steel mb-1">Desired outcome</label>
                <input type="text" value={desiredOutcome} onChange={e => setDesiredOutcome(e.target.value)}
                  placeholder="What do they want to be able to do?"
                  className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal" />
              </div>
              <div>
                <label className="block text-footnote text-steel mb-1">Objections</label>
                <textarea value={objections} onChange={e => setObjections(e.target.value)}
                  placeholder="Why did they hesitate or say no?" rows={2}
                  className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal resize-none" />
              </div>
              <div>
                <label className="block text-footnote text-steel mb-1">Questions asked</label>
                <textarea value={questionsAsked} onChange={e => setQuestionsAsked(e.target.value)}
                  placeholder="What did they ask us?" rows={2}
                  className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-footnote text-steel mb-1">Budget signal</label>
                  <select value={budgetSignal} onChange={e => setBudgetSignal(e.target.value)}
                    className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
                    <option value="">Unknown</option>
                    <option value="none">None</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-footnote text-steel mb-1">Timeline</label>
                  <select value={timeline} onChange={e => setTimeline(e.target.value)}
                    className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
                    <option value="">Unknown</option>
                    <option value="immediate">Immediate</option>
                    <option value="3mo">3 months</option>
                    <option value="6mo">6 months</option>
                    <option value="12mo+">12 months+</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-footnote text-steel mb-1">Design partner interest</label>
                  <select value={designPartnerInterest} onChange={e => setDesignPartnerInterest(e.target.value)}
                    className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
                    <option value="">Unknown</option>
                    <option value="none">None</option>
                    <option value="low">Low</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-footnote text-steel mb-1">Signal</label>
                  <select value={winLoss} onChange={e => setWinLoss(e.target.value)}
                    className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
                    <option value="">— Not set —</option>
                    <option value="win">Win</option>
                    <option value="loss">Loss</option>
                    <option value="pending">Pending</option>
                    <option value="nurture">Nurture</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="admin_leads_demo_req" checked={demoRequested}
                  onChange={e => setDemoRequested(e.target.checked)}
                  className="w-4 h-4 rounded border-carbon bg-void text-signal focus:ring-signal" />
                <label htmlFor="admin_leads_demo_req" className="text-footnote text-steel cursor-pointer">Demo requested?</label>
              </div>
            </div>
          )}
          {error && <div className="text-footnote text-red-400 bg-red-900/20 rounded px-3 py-2">{error}</div>}
        </div>
        <div className="p-5 border-t border-carbon flex gap-3 justify-end">
          <button onClick={onClose}
            className="px-4 py-2 text-body-sm text-steel hover:text-chalk border border-carbon rounded transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-body-sm bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {saving ? 'Saving…' : 'Save Interaction'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Company grouping ─────────────────────────────────────────────────────────

interface CompanyGroup {
  company: string | null
  primary: Lead
  contacts: Lead[]
}

/**
 * Group leads by company. Within each group, the primary contact (is_primary=true)
 * comes first; then by ICP score desc. Ungrouped leads (no company) each form their
 * own group. The resulting groups are sorted by the primary contact's ICP score desc.
 */
function groupLeadsByCompany(leads: Lead[]): CompanyGroup[] {
  const map = new Map<string, Lead[]>()
  for (const lead of leads) {
    const key = lead.company?.trim().toLowerCase() ?? `__no_company_${lead.id}`
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(lead)
  }

  const groups: CompanyGroup[] = []
  for (const [, contacts] of map) {
    // Sort: primary first, then by ICP desc
    const sorted = [...contacts].sort((a, b) => {
      if (a.is_primary && !b.is_primary) return -1
      if (!a.is_primary && b.is_primary) return 1
      return (b.icp_total ?? 0) - (a.icp_total ?? 0)
    })
    groups.push({
      company: sorted[0].company,
      primary: sorted[0],
      contacts: sorted,
    })
  }

  // Sort groups by primary contact ICP desc
  groups.sort((a, b) => (b.primary.icp_total ?? 0) - (a.primary.icp_total ?? 0))
  return groups
}

// ─── CRM Grid Table ───────────────────────────────────────────────────────────
// Shared column definitions — widths are mutable via drag resize

const LEAD_COLS = [
  { key: 'company',       label: 'Company / Contact', defaultW: 220, sortable: true },
  { key: 'vertical',      label: 'Vertical',          defaultW: 110, sortable: true },
  { key: 'status',        label: 'Status',            defaultW: 90,  sortable: true },
  { key: 'signal',        label: 'Signal',            defaultW: 80,  sortable: true },
  { key: 'icp',           label: 'ICP',               defaultW: 52,  sortable: true },
  { key: 'last_touch',    label: 'Last touch',        defaultW: 100, sortable: true },
  { key: 'next_action',   label: 'Next action',       defaultW: 160, sortable: false },
  { key: 'actions',       label: '',                  defaultW: 100, sortable: false },
]

function buildGridTemplate(widths: number[]): string {
  return widths.map((w, i) => i === widths.length - 2 ? `minmax(${w}px,1fr)` : `${w}px`).join(' ')
}

// ─── Expanded group panel ─────────────────────────────────────────────────────

function GroupExpandPanel({
  group,
  onEdit,
  onLogInteraction,
  onClose,
  onMakePrimary,
}: {
  group: CompanyGroup
  onEdit: (l: Lead) => void
  onLogInteraction: (l: Lead) => void
  onClose: () => void
  onMakePrimary: (l: Lead) => Promise<void>
}) {
  const c = useThemeColors()
  const primary = group.primary
  const [noteText, setNoteText] = useState('')
  const [saving, setSaving] = useState(false)
  const [noteSaved, setNoteSaved] = useState(false)
  const [savedNotes, setSavedNotes] = useState<{ text: string; ts: string }[]>([])
  const [makingPrimary, setMakingPrimary] = useState<string | null>(null)

  async function handleLogNote() {
    if (!noteText.trim()) return
    setSaving(true)
    try {
      await logInteraction(primary.id, { channel: 'note', direction: 'outbound', notes: noteText.trim(), outcome: 'follow_up' } as NewInteraction)
      setSavedNotes(prev => [{ text: noteText.trim(), ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }, ...prev])
      setNoteText('')
      setNoteSaved(true)
      setTimeout(() => setNoteSaved(false), 2000)
    } catch { /* silent */ } finally { setSaving(false) }
  }

  async function handleMakePrimary(lead: Lead) {
    setMakingPrimary(lead.id)
    try {
      await onMakePrimary(lead)
    } finally { setMakingPrimary(null) }
  }

  return (
    <div style={{ borderLeft: `3px solid ${c.signal}80`, background: c.bgSecondary }}
      className="px-4 py-3 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-footnote flex-1">
          {primary.company && <div><span className="text-steel">Company: </span><span className="text-chalk">{primary.company}</span></div>}
          {primary.website && <div><span className="text-steel">Website: </span><a href={primary.website.startsWith('http') ? primary.website : `https://${primary.website}`} target="_blank" rel="noopener noreferrer" className="text-signal hover:underline">{primary.website}</a></div>}
          {primary.introduced_by && <div><span className="text-steel">Intro via: </span><span className="text-chalk">{primary.introduced_by}</span></div>}
          {primary.what_they_do && <div className="col-span-2"><span className="text-steel">About: </span><span className="text-chalk">{primary.what_they_do}</span></div>}
          {primary.next_action && <div className="col-span-2"><span className="text-steel">Next: </span><span className="text-signal">{primary.next_action}</span></div>}
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => onEdit(primary)} className="px-2 py-1 text-footnote text-steel hover:text-chalk border border-carbon rounded">✏ Edit</button>
          <button onClick={() => onLogInteraction(primary)} className="px-2 py-1 text-footnote text-signal border border-signal/30 rounded">+ Log</button>
          <button onClick={onClose} className="px-2 py-1 text-footnote text-steel hover:text-chalk border border-carbon rounded">✕</button>
        </div>
      </div>

      {/* All contacts for this company */}
      {group.contacts.length > 0 && (
        <div className="border-t border-carbon/40 pt-2">
          <div className="text-footnote text-steel uppercase tracking-wide mb-2">Contacts ({group.contacts.length})</div>
          <div className="space-y-1.5">
            {group.contacts.map(contact => (
              <div key={contact.id} className="flex items-center gap-2 py-1">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-footnote text-chalk font-medium">{contact.full_name ?? '—'}</span>
                    {contact.is_primary && (
                      <span style={{ padding: '1px 5px', fontSize: 10, background: `${c.signal}26`, color: c.signal, borderRadius: 3, fontWeight: 600, lineHeight: 1.4 }}>Primary</span>
                    )}
                    {contact.contact_title && (
                      <span className="text-footnote text-steel">{contact.contact_title}</span>
                    )}
                  </div>
                  {contact.email && !isPlaceholderEmail(contact.email) && (
                    <div className="text-footnote text-steel/70">{contact.email}</div>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  {!contact.is_primary && group.contacts.length > 1 && (
                    <button
                      onClick={() => void handleMakePrimary(contact)}
                      disabled={makingPrimary === contact.id}
                      style={{ padding: '2px 6px', fontSize: 10, color: c.textMuted, border: `1px solid ${c.border}`, borderRadius: 3, background: 'none', cursor: 'pointer' }}
                    >
                      {makingPrimary === contact.id ? '…' : 'Make Primary'}
                    </button>
                  )}
                  <button onClick={() => onEdit(contact)} style={{ padding: '2px 6px', fontSize: 10, color: c.textMuted, border: `1px solid ${c.border}`, borderRadius: 3, background: 'none', cursor: 'pointer' }}>✏</button>
                  <button onClick={() => onLogInteraction(contact)} style={{ padding: '2px 6px', fontSize: 10, color: c.signal, border: `1px solid ${c.signal}4D`, borderRadius: 3, background: 'none', cursor: 'pointer' }}>+</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <div className="flex gap-2">
          <input
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleLogNote() } }}
            placeholder="Quick note (Enter to save)…"
            className="flex-1 bg-void border border-carbon rounded px-3 py-1.5 text-footnote text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal"
          />
          <button onClick={handleLogNote} disabled={saving || !noteText.trim()}
            className="px-3 py-1.5 text-footnote bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-40 shrink-0">
            {noteSaved ? '✓' : 'Save'}
          </button>
        </div>
        {savedNotes.length > 0 && (
          <div className="space-y-1 pl-1">
            {savedNotes.map((n, i) => (
              <div key={i} className="flex gap-2 text-footnote">
                <span className="text-steel shrink-0">{n.ts} 📝</span>
                <span className="text-chalk">{n.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface LeadTableProps {
  leads: Lead[]
  onEdit: (lead: Lead) => void
  onLogInteraction: (lead: Lead) => void
  onBookingSuccess: (msg: string) => void
  onLeadsChanged: (updatedLeads: Lead[]) => void
}

function LeadTable({ leads, onEdit, onLogInteraction, onBookingSuccess, onLeadsChanged }: LeadTableProps) {
  const navigate = useNavigate()
  const c = useThemeColors()
  const [colWidths, setColWidths] = useState<number[]>(() => LEAD_COLS.map(col => col.defaultW))
  const [sortCol, setSortCol] = useState<string>('icp')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [expandedCompany, setExpandedCompany] = useState<string | null>(null)
  const [hoveredCompany, setHoveredCompany] = useState<string | null>(null)
  const dragRef = useRef<{ colIdx: number; startX: number; startW: number } | null>(null)

  // Column resize
  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      if (!dragRef.current) return
      const { colIdx, startX, startW } = dragRef.current
      const delta = e.clientX - startX
      const newW = Math.max(60, startW + delta)
      setColWidths(prev => { const next = [...prev]; next[colIdx] = newW; return next })
    }
    function onMouseUp() { dragRef.current = null }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp) }
  }, [])

  function startResize(e: React.MouseEvent, colIdx: number) {
    e.preventDefault()
    dragRef.current = { colIdx, startX: e.clientX, startW: colWidths[colIdx] }
  }

  function handleSort(key: string) {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(key); setSortDir('desc') }
  }

  // Group + sort
  const groups = groupLeadsByCompany(leads)

  // Apply sort to groups (sort groups by primary contact's sort field)
  const sortedGroups = [...groups].sort((a, b) => {
    const mul = sortDir === 'asc' ? 1 : -1
    const pa = a.primary, pb = b.primary
    switch (sortCol) {
      case 'company': return mul * ((pa.company ?? '').localeCompare(pb.company ?? ''))
      case 'vertical': return mul * ((pa.vertical ?? '').localeCompare(pb.vertical ?? ''))
      case 'status': return mul * ((pa.status ?? '').localeCompare(pb.status ?? ''))
      case 'signal': return mul * ((pa.win_loss_signal ?? '').localeCompare(pb.win_loss_signal ?? ''))
      case 'icp': return mul * ((pa.icp_total ?? 0) - (pb.icp_total ?? 0))
      case 'last_touch': {
        const da = pa.last_interaction?.occurred_at ?? ''
        const db = pb.last_interaction?.occurred_at ?? ''
        return mul * da.localeCompare(db)
      }
      default: return 0
    }
  })

  async function handleMakePrimary(lead: Lead) {
    try {
      const updated = await makeLeadPrimary(lead.id)
      // Optimistically update all leads with same company
      const newLeads = leads.map(l => {
        if (l.company === lead.company) {
          return { ...l, is_primary: l.id === lead.id }
        }
        return l
      })
      // Also update the returned lead
      onLeadsChanged(newLeads.map(l => l.id === updated.id ? { ...l, ...updated } : l))
    } catch { /* silent */ }
  }

  const gridTemplate = buildGridTemplate(colWidths)
  const cellStyle: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', minWidth: 0 }

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: colWidths.reduce((a, b) => a + b, 0) + 'px' }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: gridTemplate, background: c.bgPrimary, borderBottom: `1px solid ${c.carbon}`, position: 'sticky', top: 0, zIndex: 10 }}>
          {LEAD_COLS.map((col, i) => (
            <div key={col.key} style={{ position: 'relative', padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
              {col.sortable ? (
                <button
                  onClick={() => handleSort(col.key)}
                  style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: sortCol === col.key ? c.textPrimary : c.textMuted, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}
                >
                  {col.label}
                  {sortCol === col.key && <span style={{ fontSize: 9 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </button>
              ) : (
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: c.textMuted, fontWeight: 600 }}>{col.label}</span>
              )}
              {/* Resize handle (not on last col) */}
              {i < LEAD_COLS.length - 1 && (
                <div
                  onMouseDown={e => startResize(e, i)}
                  style={{ position: 'absolute', right: 0, top: 0, width: 4, height: '100%', cursor: 'col-resize', zIndex: 1 }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Rows — one per company group */}
        {sortedGroups.map(group => {
          const lead = group.primary
          const groupKey = group.company ?? lead.id
          const isExpanded = expandedCompany === groupKey
          const isHovered = hoveredCompany === groupKey
          const extraContacts = group.contacts.length - 1
          const signalColor = lead.win_loss_signal === 'win' ? '#4ade80' : lead.win_loss_signal === 'loss' ? '#f87171' : lead.win_loss_signal === 'nurture' ? '#fbbf24' : c.textMuted

          return (
            <div key={groupKey}>
              {/* Data row */}
              <div
                onClick={() => setExpandedCompany(isExpanded ? null : groupKey)}
                onDoubleClick={() => navigate(`/dashboard/leads/${lead.id}`)}
                onMouseEnter={() => setHoveredCompany(groupKey)}
                onMouseLeave={() => setHoveredCompany(null)}
                style={{ display: 'grid', gridTemplateColumns: gridTemplate, borderBottom: `1px solid ${c.carbon}`, cursor: 'pointer', background: isExpanded ? c.signalSubtle : isHovered ? 'rgba(255,255,255,0.03)' : undefined, transition: 'background 150ms' }}
              >
                {/* Company / Primary Contact */}
                <div style={{ ...cellStyle, padding: '7px 8px', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', overflow: 'hidden' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.company ?? '—'}</span>
                    {lead.website && (
                      <a href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 10, color: c.textMuted, flexShrink: 0, textDecoration: 'none' }}>↗</a>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', overflow: 'hidden' }}>
                    {(lead.full_name || lead.contact_title) && (
                      <span style={{ fontSize: 11, color: c.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lead.is_primary && (
                          <span style={{ marginRight: 4, padding: '1px 4px', fontSize: 9, background: 'rgba(32,88,240,0.15)', color: c.signal, borderRadius: 2, fontWeight: 600, letterSpacing: '0.03em' }}>★</span>
                        )}
                        {lead.full_name}{lead.contact_title ? ` · ${lead.contact_title}` : ''}
                      </span>
                    )}
                    {extraContacts > 0 && (
                      <span style={{ fontSize: 10, color: c.textMuted, opacity: 0.6, flexShrink: 0 }}>+{extraContacts} more</span>
                    )}
                  </div>
                </div>

                {/* Vertical */}
                <div style={{ ...cellStyle, padding: '7px 8px' }}>
                  {lead.vertical && (
                    <span style={{ fontSize: 11, fontWeight: 500, background: 'rgba(32,88,240,0.12)', color: c.signal, padding: '1px 6px', borderRadius: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                      {lead.vertical}
                    </span>
                  )}
                </div>

                {/* Status */}
                <div style={{ ...cellStyle, padding: '7px 8px' }}>
                  <span style={{ fontSize: 12, textTransform: 'capitalize', color: lead.status === 'engaged' ? '#4ade80' : lead.status === 'disqualified' ? 'rgba(248,113,113,0.7)' : c.textMuted }}>
                    {lead.status ?? '—'}
                  </span>
                </div>

                {/* Signal */}
                <div style={{ ...cellStyle, padding: '7px 8px' }}>
                  {lead.win_loss_signal && (
                    <span style={{ fontSize: 12, textTransform: 'capitalize', color: signalColor }}>{lead.win_loss_signal}</span>
                  )}
                </div>

                {/* ICP */}
                <div style={{ ...cellStyle, padding: '7px 8px', justifyContent: 'center' }}>
                  {lead.icp_total !== undefined && (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: lead.icp_total >= 22 ? 'rgba(34,197,94,0.15)' : lead.icp_total >= 18 ? 'rgba(245,158,11,0.15)' : c.carbon, color: lead.icp_total >= 22 ? '#4ade80' : lead.icp_total >= 18 ? '#fbbf24' : c.textMuted }}>
                      {lead.icp_total}
                    </span>
                  )}
                </div>

                {/* Last touch */}
                <div style={{ ...cellStyle, padding: '7px 8px', justifyContent: 'flex-end' }}>
                  {lead.last_interaction ? (
                    <span style={{ fontSize: 11, color: c.textMuted }}>
                      {CHANNEL_ICONS[lead.last_interaction.channel] ?? '💬'} {timeAgo(lead.last_interaction.occurred_at)}
                    </span>
                  ) : <span style={{ fontSize: 11, color: c.carbon }}>—</span>}
                </div>

                {/* Next action */}
                <div style={{ ...cellStyle, padding: '7px 8px' }}>
                  {lead.next_action && (
                    <span style={{ fontSize: 11, color: c.signal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>▶ {lead.next_action}</span>
                  )}
                </div>

                {/* Actions */}
                <div style={{ ...cellStyle, padding: '7px 8px', gap: 4, opacity: isHovered ? 1 : 0, transition: 'opacity 120ms' }}>
                  <ActionButtons lead={lead} onEdit={onEdit} onLogInteraction={onLogInteraction} onBookingSuccess={onBookingSuccess} navigate={navigate} />
                </div>
              </div>

              {/* Expanded group panel */}
              {isExpanded && (
                <GroupExpandPanel
                  group={group}
                  onEdit={onEdit}
                  onLogInteraction={onLogInteraction}
                  onClose={() => setExpandedCompany(null)}
                  onMakePrimary={handleMakePrimary}
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Action buttons extracted to avoid hook-in-loop
function ActionButtons({ lead, onEdit, onLogInteraction, onBookingSuccess, navigate }: {
  lead: Lead
  onEdit: (l: Lead) => void
  onLogInteraction: (l: Lead) => void
  onBookingSuccess: (msg: string) => void
  navigate: ReturnType<typeof useNavigate>
}) {
  const c = useThemeColors()
  const [calcomSending, setCalcomSending] = useState(false)

  async function handleBooking(e: React.MouseEvent) {
    e.stopPropagation()
    setCalcomSending(true)
    try {
      const result = await triggerCalcomEngagedLead(lead.id)
      onBookingSuccess(`✓ Booking link sent to ${result.to}`)
    } catch (err) {
      const error = err as { status?: number; message?: string }
      onBookingSuccess(error.status === 409 ? '⚠ Already booked' : `✗ Failed — ${error.message ?? 'error'}`)
    } finally { setCalcomSending(false) }
  }

  return (
    <>
      <button onClick={() => onEdit(lead)} title="Edit" style={{ padding: '2px 7px', fontSize: 11, color: c.textMuted, border: `1px solid ${c.border}`, borderRadius: 3, background: 'none', cursor: 'pointer' }}>✏</button>
      <button onClick={() => onLogInteraction(lead)} title="Log interaction" style={{ padding: '2px 7px', fontSize: 11, color: c.signal, border: `1px solid ${c.signalSubtle}`, borderRadius: 3, background: 'none', cursor: 'pointer' }}>+</button>
      <button onClick={() => navigate(`/dashboard/leads/${lead.id}`)} title="Full detail" style={{ padding: '2px 7px', fontSize: 11, color: c.textMuted, border: `1px solid ${c.border}`, borderRadius: 3, background: 'none', cursor: 'pointer' }}>→</button>
      {lead.status === 'engaged' && lead.email && !isPlaceholderEmail(lead.email) && (
        <button onClick={e => void handleBooking(e)} disabled={calcomSending} title="Send booking link" style={{ padding: '2px 7px', fontSize: 11, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: 3, background: c.carbon, cursor: 'pointer' }}>{calcomSending ? '…' : '📅'}</button>
      )}
    </>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminLeads() {
  const { user, isLoaded } = useUser()
  const [leads, setLeads] = useState<Lead[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [verticalFilter, setVerticalFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<string>('score')
  const [search, setSearch] = useState('')
  const [logModal, setLogModal] = useState<Lead | null>(null)
  const [showAddLead, setShowAddLead] = useState(false)
  const [editLead, setEditLead] = useState<Lead | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  const email = user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? ''
  const isAdmin = isLoaded && !!user && ADMIN_EMAILS.includes(email)

  const loadLeads = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getAdminLeads({
        status: statusFilter,
        vertical: verticalFilter === 'all' ? undefined : verticalFilter,
        sort: sortBy,
      })
      setLeads(data)
    } catch {
      // silent
    } finally {
      setLoading(false)
    }
  }, [statusFilter, verticalFilter, sortBy])

  useEffect(() => {
    if (isAdmin) void loadLeads()
  }, [isAdmin, loadLeads])

  const filteredLeads = search.trim()
    ? leads.filter(l =>
        l.company?.toLowerCase().includes(search.toLowerCase()) ||
        l.full_name?.toLowerCase().includes(search.toLowerCase()),
      )
    : leads

  const groupCount = groupLeadsByCompany(filteredLeads).length

  if (!isLoaded) return <div className="min-h-screen bg-void" />

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <p className="text-body text-steel">Access denied</p>
      </div>
    )
  }

  const STATUS_TABS = ['all', 'prospect', 'engaged', 'disqualified']
  const SORT_OPTIONS = [
    { value: 'score', label: 'Score ↓' },
    { value: 'created_at', label: 'Recent' },
    { value: 'company', label: 'Company A-Z' },
  ]

  function handleLeadCreated(lead: Lead) {
    setShowAddLead(false)
    setLeads(prev => [lead, ...prev])
    setToast('Lead added — Clay will enrich shortly')
    setTimeout(() => setToast(null), 4000)
  }

  function handleLeadSaved(updated: Lead) {
    setLeads(prev => prev.map(l => l.id === updated.id ? updated : l))
    setEditLead(null)
    setToast('Saved')
    setTimeout(() => setToast(null), 3000)
  }

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 4000)
  }

  return (
    <>
      {showAddLead && (
        <AddLeadModal
          onClose={() => setShowAddLead(false)}
          onSuccess={handleLeadCreated}
        />
      )}

      {editLead && (
        <EditLeadPanel
          lead={editLead}
          onClose={() => setEditLead(null)}
          onSaved={handleLeadSaved}
        />
      )}

      {logModal && (
        <LogInteractionModal
          leadId={logModal.id}
          leadName={`${logModal.full_name ?? logModal.company ?? 'Lead'}`}
          onClose={() => setLogModal(null)}
          onSuccess={() => void loadLeads()}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-signal text-white text-body-sm px-4 py-3 rounded shadow-lg">
          {toast}
        </div>
      )}

      <div className="flex-1 p-6 text-chalk">
        <div className="max-w-6xl space-y-4">

          {/* Header */}
          <div className="flex items-center gap-3">
            <h1 className="text-h1 text-chalk">Customer CRM</h1>
            {!loading && (
              <span className="px-2 py-0.5 rounded text-footnote bg-carbon text-steel font-medium" title={`${filteredLeads.length} contacts`}>
                {groupCount} {groupCount === filteredLeads.length ? '' : <span className="opacity-60 ml-1">({filteredLeads.length})</span>}
              </span>
            )}
            <button onClick={loadLeads} className="ml-auto text-body-sm text-signal hover:underline">
              Refresh
            </button>
            <button
              onClick={() => setShowAddLead(true)}
              className="px-3 py-1.5 text-body-sm bg-signal text-white rounded hover:bg-signal/90 transition-colors"
            >
              + Add Lead
            </button>
          </div>

          {/* Filter bar */}
          <div className="space-y-2">
            <div className="flex gap-2">
              {STATUS_TABS.map(tab => (
                <button key={tab} onClick={() => setStatusFilter(tab)}
                  className={`text-body-sm capitalize px-3 py-1 rounded transition-colors ${
                    statusFilter === tab ? 'bg-signal text-white' : 'text-steel hover:text-chalk'
                  }`}>
                  {tab}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <select value={verticalFilter} onChange={e => setVerticalFilter(e.target.value)}
                className="bg-obsidian border border-carbon rounded px-3 py-1.5 text-body-sm text-chalk focus:outline-none focus:border-signal">
                <option value="all">All Verticals</option>
                {VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                className="bg-obsidian border border-carbon rounded px-3 py-1.5 text-body-sm text-chalk focus:outline-none focus:border-signal">
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search company or name…"
                className="flex-1 min-w-48 bg-obsidian border border-carbon rounded px-3 py-1.5 text-body-sm text-chalk placeholder:text-steel focus:outline-none focus:border-signal" />
            </div>
          </div>

          {/* Lead table */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-signal" />
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-steel text-body">No leads found.</p>
            </div>
          ) : (
            <div className="border border-carbon rounded overflow-hidden">
              <LeadTable
                leads={filteredLeads}
                onEdit={setEditLead}
                onLogInteraction={setLogModal}
                onBookingSuccess={showToast}
                onLeadsChanged={setLeads}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
